import { pacificToday } from '../../../shared/dates';
import type { IsoDate, Visit } from '../../../shared/types';
import { weekStart, weekToDate } from './dashboard';
import { addDays, parseIsoDate, toIsoDate } from './dates';

/** Home dashboard math (D56) — pure, over the mirror, so the Home works offline (D4). */

export type Period = 'week' | 'month';

export interface PeriodCount {
  current: number;
  previous: number;
  /** What `previous` covers, ready for "▲ 5 vs …": "last Mon–Wed", "last week", "Sep 1–10", "September". */
  vs: string;
}

const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTH_LONG = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const daysInMonth = (year: number, month0: number): number => new Date(year, month0 + 1, 0).getDate();

/** First day of the period that contains `today`. */
export const periodStart = (today: IsoDate, period: Period): IsoDate =>
  period === 'week' ? weekStart(today) : `${today.slice(0, 8)}01`;

/** Like-for-like: the period so far against the same stretch of the previous one. A month is
 *  compared whole on its last day, or when last month was shorter than today's day number. */
const monthToDate = (dates: readonly IsoDate[], today: IsoDate): PeriodCount => {
  const d = parseIsoDate(today);
  const prevYear = d.getMonth() === 0 ? d.getFullYear() - 1 : d.getFullYear();
  const prevMonth0 = (d.getMonth() + 11) % 12;
  const prevLength = daysInMonth(prevYear, prevMonth0);
  const isLastDay = d.getDate() === daysInMonth(d.getFullYear(), d.getMonth());
  const prevDays = isLastDay ? prevLength : Math.min(d.getDate(), prevLength);
  const prevStart = toIsoDate(new Date(prevYear, prevMonth0, 1));
  const prevEnd = addDays(prevStart, prevDays - 1);
  const start = periodStart(today, 'month');
  let current = 0;
  let previous = 0;
  for (const x of dates) {
    if (x >= start && x <= today) current++;
    else if (x >= prevStart && x <= prevEnd) previous++;
  }
  const short = MONTH_SHORT[prevMonth0] as string;
  const vs =
    prevDays === prevLength
      ? (MONTH_LONG[prevMonth0] as string)
      : prevDays === 1
        ? `${short} 1`
        : `${short} 1–${prevDays}`;
  return { current, previous, vs };
};

export const periodToDate = (dates: readonly IsoDate[], today: IsoDate, period: Period): PeriodCount => {
  if (period === 'month') return monthToDate(dates, today);
  const w = weekToDate(dates, today);
  return {
    current: w.thisWeek,
    previous: w.lastWeek,
    vs: w.span === 'week' ? 'last week' : `last ${w.span}`,
  };
};

/** The change as words + an arrow, so a tile never relies on color alone. */
export interface Delta {
  /** Full sentence, for screen readers and wide layouts. */
  text: string;
  /** Pill label for a tile ("▲ 5", "▼ 2", "Same"); the card states what it is compared with. */
  short: string;
  tone: 'up' | 'down' | 'flat';
}

export const deltaOf = (p: PeriodCount): Delta => {
  const diff = p.current - p.previous;
  if (diff === 0) return { text: `Same as ${p.vs}`, short: 'Same', tone: 'flat' };
  const short = `${diff > 0 ? '▲' : '▼'} ${Math.abs(diff)}`;
  return { text: `${short} vs ${p.vs}`, short, tone: diff > 0 ? 'up' : 'down' };
};

export const visitDay = (v: Visit): IsoDate => pacificToday(new Date(v.visited_at));

/** A deal closes on the day its first "closed" visit was logged — dated, unlike stage changes (no
 *  history, D36). A later visit also marked closed (install check) is not a second close. */
export const closeDates = (visits: readonly Visit[]): IsoDate[] => {
  const firstClose = new Map<string, IsoDate>();
  for (const v of visits) {
    if (v.outcome !== 'closed') continue;
    const day = visitDay(v);
    const seen = firstClose.get(v.lead_id);
    if (!seen || day < seen) firstClose.set(v.lead_id, day);
  }
  return [...firstClose.values()];
};

/** % of the period's visits where the rep talked to the owner; null when there were none. */
export const ownerTalkRate = (visits: readonly Visit[], today: IsoDate, period: Period): number | null => {
  const start = periodStart(today, period);
  const inPeriod = visits.filter((v) => {
    const day = visitDay(v);
    return day >= start && day <= today;
  });
  if (inPeriod.length === 0) return null;
  return Math.round((100 * inPeriod.filter((v) => v.spoke_with === 'owner').length) / inPeriod.length);
};

export interface WeekdayBar {
  label: string;
  /** null for the days of this week that have not happened yet. */
  current: number | null;
  previous: number;
}

const WEEKDAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

/** This week's visits per weekday next to last week's, Monday first. */
export const visitsByWeekday = (dates: readonly IsoDate[], today: IsoDate): WeekdayBar[] => {
  const counts = new Map<IsoDate, number>();
  for (const d of dates) counts.set(d, (counts.get(d) ?? 0) + 1);
  const start = weekStart(today);
  return WEEKDAY_LABELS.map((label, i) => {
    const day = addDays(start, i);
    return {
      label,
      current: day > today ? null : (counts.get(day) ?? 0),
      previous: counts.get(addDays(day, -7)) ?? 0,
    };
  });
};
